import React, { useState, useEffect, useRef } from 'react';
import * as tf from '@tensorflow/tfjs';
import * as blazeface from '@tensorflow-models/blazeface';
import { UploadCloud, AlertCircle, CheckCircle } from 'lucide-react';
import { InformationCircleIcon, ExclamationCircleIcon, CameraIcon } from '@heroicons/react/outline';
import logo from './logo.png';


function App() {
  const [model, setModel] = useState(null);
  const [blazeFaceModel, setBlazeFaceModel] = useState(null);
  const [image, setImage] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [loadingModel, setLoadingModel] = useState(true);
  const [classifying, setClassifying] = useState(false);
  const [error, setError] = useState(null);
  const [faces, setFaces] = useState([]);
  const [selectedFace, setSelectedFace] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const loadModels = async () => {
      try {
        const [loadedModel, loadedBlazeFaceModel] = await Promise.all([
          tf.loadLayersModel('/model.json'),
          blazeface.load(),
        ]);
        setModel(loadedModel);
        setBlazeFaceModel(loadedBlazeFaceModel);
      } catch (err) {
        setError('Failed to load models');
      } finally {
        setLoadingModel(false);
      }
    };

    loadModels();
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new Image();
      img.src = reader.result;
      img.onload = async () => {
        setImage(img);
        setPrediction(null);
        setSelectedFace(null);
        setCroppedImage(null);
        if (blazeFaceModel) {
          const predictions = await blazeFaceModel.estimateFaces(img, false);
          setFaces(predictions);
        }
      };
    };
    reader.readAsDataURL(file);
  };

  const handleFaceClick = (index) => {
    const { topLeft, bottomRight } = faces[index];
    const [x1, y1] = topLeft;
    const [x2, y2] = bottomRight;
    const faceWidth = x2 - x1;
    const faceHeight = y2 - y1;
  
    // Expand the crop area to include more of the head
    const expandAmount = 0.5; // 50% of the face width/height
    const expandedX1 = Math.max(0, x1 - faceWidth * expandAmount);
    const expandedY1 = Math.max(0, y1 - faceHeight * expandAmount);
    const expandedX2 = Math.min(image.width, x2 + faceWidth * expandAmount);
    const expandedY2 = Math.min(image.height, y2 + faceHeight * expandAmount);
    const expandedWidth = expandedX2 - expandedX1;
    const expandedHeight = expandedY2 - expandedY1;
  
    const canvas = document.createElement('canvas');
    canvas.width = expandedWidth;
    canvas.height = expandedHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, expandedX1, expandedY1, expandedWidth, expandedHeight, 0, 0, expandedWidth, expandedHeight);
  
    setCroppedImage(canvas.toDataURL());
    setSelectedFace(faces[index]);
    setPrediction(null); // Clear the prediction
  };

  const confirmFaceSelection = () => {
    classifyImage();
  };

  const classifyImage = async () => {
    if (!model || !croppedImage) {
      setError('Model or image not loaded');
      return;
    }

    setClassifying(true);
    setError(null);
    try {
      const img = new Image();
      img.src = croppedImage;
      img.onload = async () => {
        const tensor = tf.browser.fromPixels(img)
          .resizeNearestNeighbor([224, 224])
          .toFloat()
          .expandDims();
        const prediction = await model.predict(tensor).data();
        const predictedClass = prediction[0];
        setPrediction(predictedClass);
      };
    } catch (err) {
      setError('Failed to classify image');
    } finally {
      setClassifying(false);
    }
  };

  const getBoundingBoxStyles = (x1, y1, x2, y2, originalWidth, originalHeight) => {
    const imgWidth = imageRef.current.clientWidth;
    const imgHeight = imageRef.current.clientHeight;
    const scaleX = imgWidth / originalWidth;
    const scaleY = imgHeight / originalHeight;
    return {
      position: 'absolute',
      left: x1 * scaleX + imageRef.current.offsetLeft,
      top: y1 * scaleY + imageRef.current.offsetTop,
      width: (x2 - x1) * scaleX,
      height: (y2 - y1) * scaleY,
      border: '2px solid red',
      cursor: 'pointer',
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 py-6 flex flex-col justify-center sm:py-12">
      <div className="relative py-3 sm:max-w-xl sm:mx-auto">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 shadow-2xl transform -skew-y-6 sm:skew-y-0 sm:-rotate-6 sm:rounded-3xl"></div>
        <div className="relative px-6 py-10 bg-white shadow-2xl sm:rounded-3xl sm:p-20">
          <div className="max-w-md mx-auto">
            <div className="flex justify-center mb-6">
              <img src={logo} alt="Logo" className="w-72 h-32" />
            </div>
            <div className="flex flex-col items-center text-gray-700 mt-4">
              <div className="flex items-center mb-3">
                <ExclamationCircleIcon className="w-5 h-5 mr-2 text-yellow-600" />
                <span>Accuracy may vary based on image quality.</span>
              </div>
              <div className="flex items-center mb-3">
                <CameraIcon className="w-5 h-5 mr-2 text-green-600" />
                <span>Best results with high-quality, vertical face images.</span>
              </div>
            </div>
            <div className="divide-y divide-gray-300">
              <div className="py-8 text-lg leading-6 space-y-4 text-gray-800 sm:text-xl sm:leading-7">
                {loadingModel ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-4 border-gray-900"></div>
                    <p>Loading models...</p>
                  </div>
                ) : (
                  <>
                    {!image && (
                    <div className="flex items-center justify-center w-full">
                      <label htmlFor="dropzone-file" className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <UploadCloud className="w-12 h-12 mb-3 text-gray-400" />
                          <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                          <p className="text-xs text-gray-500">PNG, JPG or GIF (MAX. 800x400px)</p>
                        </div>
                        <input id="dropzone-file" type="file" className="hidden" onChange={handleImageUpload} accept="image/*" />
                      </label>
                    </div>
                  )}
                  {image && (
                    <>
                      <div className="flex items-center justify-center h-12">
                        <label htmlFor="dropzone-file" className="flex flex-col items-center justify-center w-full h-16 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                          <div className="flex flex-col items-center justify-center pt-1 pb-2">
                            <p className="mb-1 text-sm text-gray-500"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                            <p className="text-xs text-gray-500">PNG, JPG or GIF (MAX. 800x400px)</p>
                          </div>
                          <input id="dropzone-file" type="file" className="hidden" onChange={handleImageUpload} accept="image/*" />
                        </label>
                      </div>
                      <div className="mt-4 relative" ref={containerRef}>
                        {!selectedFace && (
                          <div className="p-4 bg-gray-100 rounded-lg text-gray-700 text-center text-sm">
                            <InformationCircleIcon className="w-5 h-5 inline-block mr-2 text-blue-600" />
                            Select a face to analyze
                          </div>
                        )}
                        {selectedFace && (
                          <div className="p-4"></div>
                        )}
                        <img ref={imageRef} src={image.src} alt="uploaded" className="mx-auto max-w-full h-auto rounded-lg shadow-md" />
                        {faces.map((face, index) => {
                          const { topLeft, bottomRight } = face;
                          const [x1, y1] = topLeft;
                          const [x2, y2] = bottomRight;
                          return (
                            <div
                              key={index}
                              onClick={() => handleFaceClick(index)}
                              style={getBoundingBoxStyles(x1, y1, x2, y2, image.width, image.height)}
                              className="border-2 border-blue-500 absolute"
                            ></div>
                          );
                        })}
                      </div>
                      {selectedFace && croppedImage && (
                        <>
                          <div className="mt-4">
                            <img src={croppedImage} alt="cropped" className="mx-auto w-56 h-56 object-cover rounded-lg shadow-md" />
                            <button
                              onClick={confirmFaceSelection}
                              disabled={classifying}
                              className="mt-4 w-full bg-blue-600 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition duration-150 ease-in-out"
                            >
                              {classifying ? 'Classifying...' : 'Confirm and Detect'}
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                    {prediction !== null && (
                      <div className={`mt-4 p-4 rounded-lg ${prediction > 0.5 ? 'bg-red-100' : 'bg-green-100'}`}>
                        <h2 className="text-xl font-semibold flex items-center">
                          {prediction > 0.5 ? (
                            <>
                              <AlertCircle className="w-6 h-6 mr-2 text-red-600" />
                              <span className="text-red-800">Deepfake Detected</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-6 h-6 mr-2 text-green-600" />
                              <span className="text-green-800">Image Appears Real</span>
                            </>
                          )}
                        </h2>
                        <p className="mt-2 text-sm">Confidence: {(Math.abs(prediction - 0.5) * 200).toFixed(2)}%</p>
                        <p className="mt-2 text-sm text-gray-600">Please note that this result is not 100% accurate and should not be used for commercial or production use.</p>
                      </div>
                    )}
                    {error && (
                      <div className="mt-4 p-4 bg-red-100 rounded-lg">
                        <h2 className="text-xl font-semibold text-red-800 flex items-center">
                          <AlertCircle className="w-6 h-6 mr-2" />
                          Error
                        </h2>
                        <p className="mt-2 text-sm text-red-600">{error}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <p className="text-gray-600 text-center mt-6">Powered by TensorFlow.js and BlazeFace</p>
      <p className="text-gray-600 text-center mt-2">© 2024 Joshua Ramirez | All Rights Reserved</p>
    </div>
  );
  
}

export default App;
                    